import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { saveApiData } from '../../store/Slice/Global/GlobalSlice';
import { PayableApi } from '../../utils/Apis';
import { setApiError } from '../../store/Slice/Error/ErrorSlice';

export default function PayableDialogRedux({
  openPayment,
  setOpenPayment,
  userId,
  paybleId,
  selectedPaybleId,
  onSuccess
}) {
  const dispatch = useDispatch();

  const PaymentMode = ['RTGS', 'NEFT', 'OTHER', 'UPI', 'BANK TRANSFER'];

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      paymentDate: '',
      invoiceAmount: '',
      tdsDeduction: '',
      gstPaid: '',
      netAmountPaid: '',
      paymentMode: ''
    }
  });

  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (data) => {
    const payload = {
      userId,
      paybleId: selectedPaybleId || paybleId,
      paymentDate: data.paymentDate,
      paymentMode: data.paymentMode,
      invoiceAmount: parseFloat(data.invoiceAmount),
      gstPaid: parseFloat(data.gstPaid),
      tdsDeduction: parseFloat(data.tdsDeduction),
      netAmountPaid: parseFloat(data.netAmountPaid)
    };

    if (
      Number.isNaN(payload.invoiceAmount) ||
      Number.isNaN(payload.gstPaid) ||
      Number.isNaN(payload.tdsDeduction) ||
      Number.isNaN(payload.netAmountPaid)
    ) {
      alert('Please enter valid numeric values for all amount fields.');
      return;
    }

    setSubmitting(true);
    try {
      // await dispatch(saveApiData(PayableApi?.payablePayment || 'payble/payment', payload));
      dispatch(setApiError('Paid Successfully!'));
      reset();
      setOpenPayment(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('dispatch error', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={openPayment} onClose={() => setOpenPayment(false)} fullWidth>
      <DialogTitle className="popupTitle">
        <Typography variant="h6">Payable</Typography>
        <IconButton onClick={() => setOpenPayment(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="body2" className="formLabel">
                  Payment Date
                </Typography>
                <Controller
                  name="paymentDate"
                  control={control}
                  rules={{ required: 'Payment Date is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      inputProps={{ className: 'textInput' }}
                      error={!!errors.paymentDate}
                      helperText={errors.paymentDate?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="body2" className="formLabel">
                  Invoice Amount
                </Typography>
                <Controller
                  name="invoiceAmount"
                  control={control}
                  rules={{
                    required: 'Invoice Amount is required',
                    validate: (v) =>
                      (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      inputProps={{ className: 'textInput' }}
                      type="number"
                      error={!!errors.invoiceAmount}
                      helperText={errors.invoiceAmount?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="body2" className="formLabel">
                  TDS Deduction
                </Typography>
                <Controller
                  name="tdsDeduction"
                  control={control}
                  rules={{
                    required: 'TDS Deduction is required',
                    validate: (v) =>
                      (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      inputProps={{ className: 'textInput' }}
                      type="number"
                      error={!!errors.tdsDeduction}
                      helperText={errors.tdsDeduction?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="body2" className="formLabel">
                  GST Paid
                </Typography>
                <Controller
                  name="gstPaid"
                  control={control}
                  rules={{
                    required: 'GST Paid is required',
                    validate: (v) =>
                      (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      inputProps={{ className: 'textInput' }}
                      type="number"
                      error={!!errors.gstPaid}
                      helperText={errors.gstPaid?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="body2" className="formLabel">
                  Net Amount Paid
                </Typography>
                <Controller
                  name="netAmountPaid"
                  control={control}
                  rules={{
                    required: 'Net Amount Paid is required',
                    validate: (v) =>
                      (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      inputProps={{ className: 'textInput' }}
                      type="number"
                      error={!!errors.netAmountPaid}
                      helperText={errors.netAmountPaid?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="body2" className="formLabel">
                  Payment Mode
                </Typography>
                <Controller
                  name="paymentMode"
                  control={control}
                  rules={{ required: 'Payment Mode is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      size="small"
                      displayEmpty
                      renderValue={(selected) =>
                        selected ? selected : <span style={{ color: '#888' }}>Select option</span>
                      }
                    >
                      <MenuItem value="RTGS">RTGS</MenuItem>
                      <MenuItem value="NEFT">NEFT</MenuItem>
                      <MenuItem value="OTHER">OTHER</MenuItem>
                      <MenuItem value="UPI">UPI</MenuItem>
                      <MenuItem value="BANK TRANSFER">BANK TRANSFER</MenuItem>
                    </Select>
                  )}
                />
                {errors.paymentMode && (
                  <Typography variant="caption" color="error">
                    {errors.paymentMode.message}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenPayment(false)} variant="outlined" disabled={submitting}>
            Cancel
          </Button>

          <Button type="submit" variant="contained" className="mainBtn" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
